import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { createEntity, updateEntity, friendlyError } from '../data/entities';

// The ONE "new workflow" form, shared by nQube, the workspace page, the
// pipeline editor header and Overview. Two lessons baked in (Mina, 2026-09-30):
//   - a rejected write must say so inline — the old forms were try/finally
//     with no catch, so a failure looked like "nothing happened";
//   - an org with no workspace yet gets a default "Main" one created on the
//     first submit instead of being sent away to make one by hand.
export const DEFAULT_WORKSPACE_NAME = 'Main';

// Kept for existing importers; the helper lives in data/entities.js now.
export const friendlyWriteError = friendlyError;

export default function NewWorkflowForm({
  orgId,
  workspaceId = null,       // null → first (or default) workspace of the org
  workspaces = null,        // live list from useEntityList('workspace'), when known
  placeholder = 'New workflow name…',
  buttonLabel = 'Create',
  small = false,
  style = {},
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const appId = params.get('app');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setError('');
    try {
      let ws = workspaceId || workspaces?.[0]?.id || null;
      if (!ws) {
        ws = await createEntity('workspace', { org: orgId }, {
          name: DEFAULT_WORKSPACE_NAME,
          description: 'Created automatically with your first workflow.',
          createdBy: user.uid,
        });
      }
      const scope = { org: orgId, workspace: ws };
      const id = await createEntity('pipeline', scope, {
        name: name.trim(),
        createdBy: user.uid,
        execution: { profile: 'economy' },
      });
      // Created from a module scoped to a builder app (?app=) → the new
      // workflow becomes that app's workflow (merge keeps the other links).
      if (appId) {
        try {
          await updateEntity('app', { org: orgId }, appId, { links: { workspaceId: ws, pipelineId: id } });
        } catch (linkErr) {
          setError(`Workflow created, but linking it to the app failed: ${friendlyError(linkErr)}`);
          setName('');
          return;
        }
      }
      setName('');
      navigate(`/orgs/${orgId}/ws/${ws}/pipelines/${id}${appId ? `?app=${appId}` : ''}`);
    } catch (err) {
      setError(friendlyWriteError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 480, ...style }} onSubmit={submit}>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          className="cs-input"
          placeholder={placeholder}
          value={name}
          aria-label={placeholder}
          onChange={(e) => { setName(e.target.value); if (error) setError(''); }}
        />
        <button
          className={`cs-btn cs-btn-primary ${small ? 'cs-btn-sm' : ''}`}
          disabled={!name.trim() || busy}
          type="submit"
          title={!name.trim() ? 'Type a name first' : undefined}
        >
          {busy ? 'Creating…' : buttonLabel}
        </button>
      </div>
      {error && <p className="cs-form-error" role="alert">{error}</p>}
    </form>
  );
}
